/**
 * Web Notification Service
 * Pure service for browser notifications (no React dependencies)
 * Handles permission management and notification creation
 */

/**
 * Check if browser notifications are supported
 * @returns {boolean}
 */
export function isNotificationSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * Get current notification permission status
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function getNotificationPermission() {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Request notification permission from user
 * @returns {Promise<'granted' | 'denied' | 'default' | 'unsupported'>}
 */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }

  // If already granted or denied, return current status
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }

  try {
    const result = await Notification.requestPermission();
    return result;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'default';
  }
}

/**
 * Create and show a browser notification
 * @param {Object} options
 * @param {string} options.title - Notification title
 * @param {string} options.body - Notification body text
 * @param {string} [options.icon] - Icon URL (optional)
 * @param {Object} [options.data] - Custom data to attach (optional)
 * @param {Function} [options.onClick] - Click handler (notification, event, data)
 * @returns {Notification|null} - Notification object or null if failed
 */
export function createBrowserNotification({ title, body, icon, data, onClick }) {
  if (!isNotificationSupported()) {
    console.warn('Browser notifications are not supported');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted:', Notification.permission);
    return null;
  }

  try {
    // Create completely unique tag for each notification
    // This ensures each notification shows independently, even when other websites have notifications
    // Using timestamp + random string ensures no two notifications share the same tag
    const uniqueTag = `sas-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const notification = new Notification(title, {
      body: body || '',
      icon: icon || '/favicon.ico', // Default icon
      badge: '/favicon.ico',
      tag: uniqueTag, // Completely unique tag - each notification is independent
      data: data || null,
      requireInteraction: false, // Auto-close after default timeout
      renotify: true, // Ensure notification shows even if there are other notifications
      silent: false, // Ensure sound plays
    });

    // Handle click event
    notification.onclick = (event) => {
      // Note: intentionally not calling event.preventDefault() here — doing so
      // suppresses the browser's native "restore and focus the window that
      // owns this notification" behavior, which is what un-minimizes the
      // window when it was minimized. window.focus() alone cannot reliably
      // un-minimize a native window in every browser.

      // Focus the window
      if (typeof window !== 'undefined' && window.focus) {
        window.focus();
      }

      // Call custom onClick handler if provided
      // Pass both conversationId and tabName from notification data
      if (onClick) {
        const notificationData = notification.data || {};
        onClick(notification, event, notificationData);
      }

      // Close the notification
      notification.close();
    };

    // Handle error event
    notification.onerror = (error) => {
      console.error('Notification error:', error);
    };

    // Auto-close after 5 seconds if not clicked
    setTimeout(() => {
      notification.close();
    }, 5000);

    return notification;
  } catch (error) {
    console.error('Error creating browser notification:', error);
    return null;
  }
}

/**
 * Close all notifications for a specific conversation
 * @param {number|string} conversationId
 */
export function closeConversationNotifications(conversationId) {
  // Note: Browser API doesn't provide a way to close specific notifications
  // This is a placeholder for future implementation if needed
  // Currently, notifications auto-close after 5 seconds or when clicked
}

